import { TEXT_MESSAGES_PATH } from './text-message-policy';

/**
 * PD-17174 — what a storefront tells search engines, shared by the three
 * places that say it:
 *
 *   1. crm-api's public SEO endpoints (`api/portal/seo/*`), which the
 *      CloudFront edge function calls to put real head tags and a real HTTP
 *      status into the HTML a crawler receives;
 *   2. the storefront SPA, which keeps the same tags current as the shopper
 *      moves between pages (and is what a crawler that renders JavaScript
 *      ends up reading);
 *   3. robots.txt, whose Disallow lines come from the same route table.
 *
 * Keeping the rules here means the edge-served HTML and the page the SPA
 * renders can never disagree about a title, a canonical URL or whether a page
 * may be indexed.
 */

/**
 * How search engines should treat a storefront route:
 *   - `indexable` — public catalog content worth listing in search results:
 *     the guest home (dashboard), the catalog and product pages.
 *   - `public` — reachable without signing in but not worth indexing (sign-in,
 *     registration, the app page, texting terms, promotions that expire):
 *     `noindex`, links still followed.
 *   - `private` — signed-in or token-bearing pages (orders, invoices, payment
 *     and password links): `noindex, nofollow`, and robots.txt asks crawlers
 *     not to fetch them at all, so a token in a URL is never crawled.
 */
export type StorefrontRouteSeoKind = 'indexable' | 'public' | 'private';

export interface StorefrontRouteModel {
	/** React Router style: `/product/:id`. Parameters match one path segment. */
	pattern: string;
	seo: StorefrontRouteSeoKind;
}

/**
 * Every route the storefront SPA (apps/ecommerce-web/src/App.tsx) serves. A
 * path that matches none of them is a real 404: the SPA shows its not-found
 * page and the edge returns HTTP 404. `storefront-seo.test.ts` reads App.tsx
 * and fails when the two lists drift apart, so a new storefront route must be
 * added here too.
 */
export const STOREFRONT_ROUTES: ReadonlyArray<StorefrontRouteModel> = [
	// The guest home. `/` sends a guest to `/dashboard` on a storefront that
	// allows guest browsing (HomePage), so `/dashboard` is the home page's
	// canonical address; `/` itself is the sign-in screen everywhere else.
	{ pattern: '/', seo: 'indexable' },
	{ pattern: '/dashboard', seo: 'indexable' },
	{ pattern: '/catalog', seo: 'indexable' },
	{ pattern: '/product/:id', seo: 'indexable' },
	// Promotions end, and a search result for an expired one is a dead end, so
	// they are followed (their items are linked) but not indexed.
	{ pattern: '/promos/:id', seo: 'public' },
	{ pattern: '/login', seo: 'public' },
	{ pattern: '/register', seo: 'public' },
	{ pattern: '/forgot-password', seo: 'public' },
	{ pattern: '/app', seo: 'public' },
	{ pattern: TEXT_MESSAGES_PATH, seo: 'public' },
	{ pattern: '/account-deleted', seo: 'public' },
	{ pattern: '/select-client', seo: 'private' },
	{ pattern: '/reset-password', seo: 'private' },
	{ pattern: '/set-password', seo: 'private' },
	{ pattern: '/sign-in-link', seo: 'private' },
	{ pattern: '/s/:code', seo: 'private' },
	{ pattern: '/pay/:token', seo: 'private' },
	{ pattern: '/payment-method/:token', seo: 'private' },
	{ pattern: '/form/:token', seo: 'private' },
	// PD-17646 — booking pages open from a link; they are not storefront content to index.
	{ pattern: '/book/meeting/:token', seo: 'private' },
	{ pattern: '/book/booth', seo: 'private' },
	{ pattern: '/book/booth/:kit', seo: 'private' },
	{ pattern: '/book/:person/:type', seo: 'private' },
	{ pattern: '/book/:person', seo: 'private' },
	{ pattern: '/cart', seo: 'private' },
	{ pattern: '/orders', seo: 'private' },
	{ pattern: '/orders/:id', seo: 'private' },
	{ pattern: '/orders/:id/line/:lineId', seo: 'private' },
	{ pattern: '/invoices', seo: 'private' },
	{ pattern: '/invoices/:id', seo: 'private' },
	{ pattern: '/invoices/:id/line/:lineId', seo: 'private' },
	{ pattern: '/statements', seo: 'private' },
	{ pattern: '/statements/:id', seo: 'private' },
	{ pattern: '/transactions', seo: 'private' },
	{ pattern: '/transactions/:id', seo: 'private' },
	{ pattern: '/payments/make', seo: 'private' },
	{ pattern: '/support', seo: 'private' },
	{ pattern: '/support/:id', seo: 'private' },
	{ pattern: '/account', seo: 'private' },
	{ pattern: '/account/payment-methods', seo: 'private' },
	{ pattern: '/account/password', seo: 'private' },
	{ pattern: '/account/contact', seo: 'private' },
	{ pattern: '/account/notifications', seo: 'private' },
];

/** The product page path for an item. */
export function storefrontProductPath(itemId: number | string): string {
	return `/product/${itemId}`;
}

/**
 * The path part of a storefront URL as the route table sees it: no query or
 * fragment, no repeated or trailing slashes, and `/index.html` read as `/`
 * (CloudFront's default root object, and a path a native shell could load).
 */
export function normalizeStorefrontPath(rawPath: string): string {
	const pathOnly = rawPath.split(/[?#]/)[0] ?? '';
	const collapsed = `/${pathOnly}`.replace(/\/{2,}/g, '/');
	const trimmed =
		collapsed.length > 1 ? collapsed.replace(/\/+$/, '') : collapsed;
	if (trimmed === '' || trimmed === '/index.html') {
		return '/';
	}
	return trimmed;
}

export interface StorefrontRouteMatchModel {
	route: StorefrontRouteModel;
	params: Record<string, string>;
}

/** The storefront route a path belongs to, or null when there is none (a 404). */
export function matchStorefrontRoute(
	rawPath: string,
): StorefrontRouteMatchModel | null {
	const path = normalizeStorefrontPath(rawPath);
	const segments = path.split('/').filter((segment) => segment !== '');

	for (const route of STOREFRONT_ROUTES) {
		const patternSegments = route.pattern
			.split('/')
			.filter((segment) => segment !== '');
		if (patternSegments.length !== segments.length) {
			continue;
		}

		const params: Record<string, string> = {};
		let matched = true;
		for (let index = 0; index < patternSegments.length; index++) {
			const patternSegment = patternSegments[index] ?? '';
			const segment = segments[index] ?? '';
			if (patternSegment.startsWith(':')) {
				params[patternSegment.slice(1)] = segment;
			} else if (patternSegment !== segment) {
				matched = false;
				break;
			}
		}

		if (matched) {
			return { route, params };
		}
	}

	return null;
}

/**
 * The robots.txt `Disallow` paths: one per private route, cut at its first
 * parameter (`/pay/:token` → `/pay/`), with any path already covered by a
 * shorter one dropped (`/orders` covers `/orders/`). A parameter prefix keeps
 * its trailing slash, so `/s/` never blocks `/statements` or `/support`
 * through a bare `/s`.
 */
export function storefrontRobotsDisallowPaths(): Array<string> {
	const prefixes = STOREFRONT_ROUTES.filter(
		(route) => route.seo === 'private',
	).map((route) => {
		const paramIndex = route.pattern.indexOf('/:');
		return paramIndex === -1
			? route.pattern
			: route.pattern.slice(0, paramIndex + 1);
	});

	const unique = Array.from(new Set(prefixes)).sort(
		(a, b) => a.length - b.length || a.localeCompare(b),
	);
	const kept: Array<string> = [];
	for (const prefix of unique) {
		if (!kept.some((shorter) => prefix.startsWith(shorter))) {
			kept.push(prefix);
		}
	}
	return kept.sort((a, b) => a.localeCompare(b));
}

/**
 * Whether search engines may index a storefront at all:
 *   - `indexable` — production, and the brand allows search engine indexing
 *     on a storefront guests can browse;
 *   - `noindex` — production, but the brand does not (every page says
 *     `noindex`, crawling stays allowed so already-indexed pages drop out);
 *   - `blocked` — not production. Staging storefronts must never be
 *     searchable, whatever their settings: every page says
 *     `noindex, nofollow` and robots.txt disallows everything.
 */
export type StorefrontIndexingState = 'indexable' | 'noindex' | 'blocked';

/**
 * Whether the brand's own settings ask for indexing: the setting is on AND
 * guests can browse a catalog. Search engines are guests, so a storefront that
 * needs a sign-in has nothing for them to index. Takes already-resolved
 * settings (the resolver forces guest browsing off without a catalog).
 */
export function wantsStorefrontIndexing(settings: {
	allowSearchEngineIndexing: boolean;
	allowAnonymousBrowsing: boolean;
	allowCatalogBrowsing: boolean;
}): boolean {
	return (
		settings.allowSearchEngineIndexing &&
		settings.allowAnonymousBrowsing &&
		settings.allowCatalogBrowsing
	);
}

export function resolveStorefrontIndexingState(input: {
	isProduction: boolean;
	wantsIndexing: boolean;
}): StorefrontIndexingState {
	if (!input.isProduction) {
		return 'blocked';
	}
	return input.wantsIndexing ? 'indexable' : 'noindex';
}

/** The storefront itself, as its head tags describe it. */
export interface StorefrontSeoSiteModel {
	/** The brand name shown on the storefront. */
	name: string;
	/** Absolute logo URL, used as the share image when a page has no picture. */
	logoUrl: string | null;
	/**
	 * `https://<host>` of the storefront's PRIMARY public address: its live
	 * custom domain when it has one, else its workd subdomain. Every URL the
	 * tags carry (canonical, og:url, sitemap entries) uses it, whichever of the
	 * storefront's hosts the page was requested on, so search engines
	 * consolidate the hosts into one listing.
	 */
	origin: string;
	indexingState: StorefrontIndexingState;
}

/**
 * A product as a guest sees it. Every field is what the guest catalog already
 * serves: `ndc` only when the storefront shows NDCs, `price` only when guests
 * are shown prices (and then the price the product page shows, promotions
 * applied), `inStock` only when guests are shown stock.
 */
export interface StorefrontSeoProductModel {
	id: number;
	name: string;
	description: string | null;
	imageUrl: string | null;
	sku: string | null;
	ndc: string | null;
	brand: string | null;
	price: number | null;
	inStock: boolean | null;
}

export type StorefrontSeoTranslate = (
	key: string,
	params?: Record<string, string>,
) => string;

export interface StorefrontHeadModel {
	/** 404 when the path is no storefront route, or the product is not one a guest can see. */
	status: 200 | 404;
	title: string;
	description: string;
	canonical: string | null;
	robots: string;
	/** og:url — the canonical address when there is one, else the page's own. */
	url: string;
	image: string | null;
	ogType: 'website' | 'product';
	/** schema.org Product, on product pages only. */
	jsonLd: Record<string, unknown> | null;
}

export interface BuildStorefrontHeadInput {
	path: string;
	site: StorefrontSeoSiteModel;
	/**
	 * Product pages only: the product, null when no guest can see it (a 404),
	 * or undefined when it was not looked up (a storefront without guest
	 * browsing, where only signed-in shoppers can see products).
	 */
	product?: StorefrontSeoProductModel | null;
	/** Marks the page as not found whatever the route says (the SPA's own 404s). */
	notFound?: boolean;
	/** Defaults to English with `{{name}}` interpolation; the SPA passes t(). */
	translate?: StorefrontSeoTranslate;
	/** ISO 4217 currency of `product.price`. Default USD. */
	currency?: string;
}

/** Meta descriptions longer than this are cut by search engines anyway. */
export const STOREFRONT_SEO_DESCRIPTION_MAX_LENGTH = 160;

const defaultTranslate: StorefrontSeoTranslate = (key, params) =>
	params == null
		? key
		: key.replace(/\{\{(\w+)\}\}/g, (placeholder, name: string) =>
				Object.prototype.hasOwnProperty.call(params, name)
					? (params[name] ?? '')
					: placeholder,
			);

const HTML_ENTITIES: Record<string, string> = {
	amp: '&',
	lt: '<',
	gt: '>',
	quot: '"',
	apos: "'",
	nbsp: ' ',
};

/**
 * Plain, single-line text of at most `maxLength` characters, cut at a word
 * where possible. An item's extended description is free text that can hold
 * HTML from an ERP, so tags are dropped and the common entities decoded.
 */
export function toSeoPlainText(
	value: string | null | undefined,
	maxLength: number = STOREFRONT_SEO_DESCRIPTION_MAX_LENGTH,
): string {
	if (value == null) {
		return '';
	}
	const text = value
		.replace(/<[^>]*>/g, ' ')
		.replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (entity, code: string) => {
			const lower = code.toLowerCase();
			if (lower.startsWith('#')) {
				const codePoint = lower.startsWith('#x')
					? parseInt(lower.slice(2), 16)
					: parseInt(lower.slice(1), 10);
				// An out-of-range reference would make fromCodePoint throw.
				return codePoint >= 1 && codePoint <= 0x10ffff
					? String.fromCodePoint(codePoint)
					: entity;
			}
			return Object.prototype.hasOwnProperty.call(HTML_ENTITIES, lower)
				? (HTML_ENTITIES[lower] ?? entity)
				: entity;
		})
		.replace(/\s+/g, ' ')
		.trim();

	if (text.length <= maxLength) {
		return text;
	}
	const cut = text.slice(0, maxLength - 1);
	const lastSpace = cut.lastIndexOf(' ');
	const atWord = lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut;
	return `${atWord.replace(/[\s.,;:!?-]+$/, '')}…`;
}

/**
 * Query parameters that mark a signed, time-limited URL: S3 / SigV4
 * presigning (what crm-api's FilesService issues for file-backed item
 * pictures), the older S3 signature, CloudFront signed URLs, Google Cloud
 * Storage and Azure SAS.
 */
const SIGNED_URL_PARAMS: ReadonlyArray<string> = [
	'x-amz-signature',
	'x-amz-credential',
	'x-amz-expires',
	'x-amz-security-token',
	'x-goog-signature',
	'key-pair-id',
];

/**
 * True for a URL that stops working after a while (a presigned or otherwise
 * signed link). Such a link must never go into a share card or structured
 * data: search engines and chat apps keep the URL long after it expires, and
 * the preview or image result then points at an error.
 */
export function isExpiringUrl(url: string): boolean {
	const queryStart = url.indexOf('?');
	if (queryStart === -1) {
		return false;
	}
	const keys = new Set<string>();
	for (const pair of url.slice(queryStart + 1).split(/[&#]/)) {
		const rawKey = pair.split('=')[0] ?? '';
		try {
			keys.add(decodeURIComponent(rawKey).toLowerCase());
		} catch {
			keys.add(rawKey.toLowerCase());
		}
	}
	if (SIGNED_URL_PARAMS.some((key) => keys.has(key))) {
		return true;
	}
	// The older S3 signature (Signature + Expires) and Azure SAS (sig + se).
	return (
		(keys.has('signature') && (keys.has('expires') || keys.has('policy'))) ||
		(keys.has('sig') && keys.has('se'))
	);
}

/**
 * An image URL safe to publish: absolute (a root-relative path is resolved
 * against the storefront), and not a signed link that will expire. Null
 * otherwise, so the caller falls back to the next picture or the logo.
 */
function stableImageUrl(origin: string, url: string | null): string | null {
	if (url == null || url.trim() === '') {
		return null;
	}
	const trimmed = url.trim();
	let absolute: string | null = null;
	if (/^https?:\/\//i.test(trimmed)) {
		absolute = trimmed;
	} else if (trimmed.startsWith('//')) {
		absolute = `https:${trimmed}`;
	} else if (trimmed.startsWith('/')) {
		absolute = `${origin}${trimmed}`;
	}
	return absolute != null && !isExpiringUrl(absolute) ? absolute : null;
}

function robotsFor(
	indexingState: StorefrontIndexingState,
	routeKind: StorefrontRouteSeoKind | null,
	status: 200 | 404,
): string {
	if (indexingState === 'blocked' || routeKind === 'private') {
		return 'noindex, nofollow';
	}
	if (indexingState === 'noindex' || status === 404 || routeKind !== 'indexable') {
		return 'noindex';
	}
	return 'index, follow';
}

/**
 * Price as schema.org wants it: a plain decimal string. A missing, zero or
 * negative price is left out, together with the whole offer: a $0 offer
 * reads to search engines as "free", which an unpriced B2B item is not.
 */
function offerPrice(price: number | null): string | null {
	if (price == null || !Number.isFinite(price) || price <= 0) {
		return null;
	}
	return price.toFixed(2);
}

function productJsonLd(
	product: StorefrontSeoProductModel,
	canonical: string,
	description: string,
	image: string | null,
	currency: string,
): Record<string, unknown> {
	const jsonLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: product.name,
		url: canonical,
	};
	if (image != null) {
		jsonLd.image = [image];
	}
	if (description !== '') {
		jsonLd.description = description;
	}
	if (product.sku != null && product.sku.trim() !== '') {
		jsonLd.sku = product.sku.trim();
	}
	if (product.ndc != null && product.ndc.trim() !== '') {
		jsonLd.identifier = {
			'@type': 'PropertyValue',
			propertyID: 'NDC',
			value: product.ndc.trim(),
		};
	}
	if (product.brand != null && product.brand.trim() !== '') {
		jsonLd.brand = { '@type': 'Brand', name: product.brand.trim() };
	}
	// Only when guests are shown the price: the caller passes null otherwise,
	// and structured data must never carry a price the page does not show.
	const price = offerPrice(product.price);
	if (price != null) {
		const offer: Record<string, unknown> = {
			'@type': 'Offer',
			price,
			priceCurrency: currency,
			url: canonical,
		};
		if (product.inStock != null) {
			offer.availability = product.inStock
				? 'https://schema.org/InStock'
				: 'https://schema.org/OutOfStock';
		}
		jsonLd.offers = offer;
	}
	return jsonLd;
}

/** The head tags for one storefront page. Pure. */
export function buildStorefrontHead(
	input: BuildStorefrontHeadInput,
): StorefrontHeadModel {
	const t = input.translate ?? defaultTranslate;
	const { site } = input;
	const path = normalizeStorefrontPath(input.path);
	const match = matchStorefrontRoute(path);
	const isProductRoute = match?.route.pattern === '/product/:id';
	const status: 200 | 404 =
		match == null ||
		input.notFound === true ||
		(isProductRoute && input.product === null)
			? 404
			: 200;
	const routeKind = match?.route.seo ?? null;
	const siteName = t(site.name);
	const pageUrl = `${site.origin}${path}`;
	const robots = robotsFor(site.indexingState, routeKind, status);
	const siteDescription = t(
		'Shop {{siteName}} online. Browse the catalog and place orders from any device.',
		{ siteName: site.name },
	);
	const logo = stableImageUrl(site.origin, site.logoUrl);

	if (status === 404) {
		return {
			status,
			title: `${t('Page not found')} | ${siteName}`,
			description: t(
				'The page you are looking for does not exist or is no longer available.',
			),
			canonical: null,
			robots,
			url: pageUrl,
			image: logo,
			ogType: 'website',
			jsonLd: null,
		};
	}

	const product = isProductRoute ? input.product : undefined;
	if (product != null) {
		const canonical = `${site.origin}${storefrontProductPath(product.id)}`;
		const description =
			toSeoPlainText(product.description) !== ''
				? toSeoPlainText(product.description)
				: toSeoPlainText(product.name);
		const productImage = stableImageUrl(site.origin, product.imageUrl);
		const image = productImage ?? logo;
		return {
			status,
			title: `${t(product.name)} | ${siteName}`,
			description,
			canonical,
			robots,
			url: canonical,
			image,
			ogType: 'product',
			jsonLd: productJsonLd(
				product,
				canonical,
				description,
				productImage,
				input.currency ?? 'USD',
			),
		};
	}

	// Indexable routes name their canonical address: the guest home is
	// `/dashboard` (where `/` sends a guest), and query strings (catalog
	// filters, search) never make a separate page.
	const canonical =
		routeKind === 'indexable'
			? `${site.origin}${path === '/' ? '/dashboard' : path}`
			: null;
	const title =
		match?.route.pattern === '/catalog'
			? `${t('Catalog')} | ${siteName}`
			: siteName;
	const description =
		match?.route.pattern === '/catalog'
			? t('Browse the {{siteName}} product catalog.', { siteName: site.name })
			: siteDescription;

	return {
		status,
		title,
		description,
		canonical,
		robots,
		url: canonical ?? pageUrl,
		image: logo,
		ogType: 'website',
		jsonLd: null,
	};
}

/**
 * One managed head element. `key` identifies the element in the document, so
 * the SPA can update the tag the edge wrote instead of adding a second one.
 */
export type StorefrontHeadTagModel =
	| { kind: 'title'; key: 'title'; text: string }
	| {
			kind: 'meta';
			key: string;
			attribute: 'name' | 'property' | 'itemprop';
			name: string;
			content: string;
	  }
	| { kind: 'canonical'; key: 'canonical'; href: string }
	| { kind: 'jsonld'; key: 'jsonld'; json: string };

/** Every key `buildStorefrontHeadTags` can emit, so a tag a page no longer needs can be removed. */
export const STOREFRONT_HEAD_TAG_KEYS: ReadonlyArray<string> = [
	'title',
	'meta:name:description',
	'meta:name:robots',
	'canonical',
	'meta:property:og:type',
	'meta:property:og:site_name',
	'meta:property:og:title',
	'meta:property:og:description',
	'meta:property:og:url',
	'meta:property:og:image',
	'meta:name:twitter:card',
	'meta:name:twitter:title',
	'meta:name:twitter:description',
	'meta:name:twitter:image',
	'meta:itemprop:name',
	'meta:itemprop:description',
	'meta:itemprop:image',
	'jsonld',
];

/**
 * JSON for an inline `<script type="application/ld+json">`. `<`, `>` and `&`
 * are escaped so text from an item description can never close the script
 * element, and the two line separators JavaScript treats as newlines are
 * escaped for older parsers.
 */
export function serializeStorefrontJsonLd(
	jsonLd: Record<string, unknown>,
): string {
	return JSON.stringify(jsonLd)
		.replace(/</g, '\\u003c')
		.replace(/>/g, '\\u003e')
		.replace(/&/g, '\\u0026')
		.replace(/\u2028/g, '\\u2028')
		.replace(/\u2029/g, '\\u2029');
}

/**
 * The tags for a head: title, description, robots, canonical, Open Graph and
 * Twitter cards (what chat apps and social sites read for link previews), the
 * `itemprop` trio WeChat reads, and the product's structured data.
 */
export function buildStorefrontHeadTags(
	head: StorefrontHeadModel,
	siteName: string,
): Array<StorefrontHeadTagModel> {
	const meta = (
		attribute: 'name' | 'property' | 'itemprop',
		name: string,
		content: string,
	): StorefrontHeadTagModel => ({
		kind: 'meta',
		key: `meta:${attribute}:${name}`,
		attribute,
		name,
		content,
	});

	const tags: Array<StorefrontHeadTagModel> = [
		{ kind: 'title', key: 'title', text: head.title },
		meta('name', 'description', head.description),
		meta('name', 'robots', head.robots),
	];
	if (head.canonical != null) {
		tags.push({ kind: 'canonical', key: 'canonical', href: head.canonical });
	}
	tags.push(
		meta('property', 'og:type', head.ogType),
		meta('property', 'og:site_name', siteName),
		meta('property', 'og:title', head.title),
		meta('property', 'og:description', head.description),
		meta('property', 'og:url', head.url),
	);
	if (head.image != null) {
		tags.push(meta('property', 'og:image', head.image));
	}
	tags.push(
		meta(
			'name',
			'twitter:card',
			head.image != null ? 'summary_large_image' : 'summary',
		),
		meta('name', 'twitter:title', head.title),
		meta('name', 'twitter:description', head.description),
	);
	if (head.image != null) {
		tags.push(meta('name', 'twitter:image', head.image));
	}
	tags.push(
		meta('itemprop', 'name', head.title),
		meta('itemprop', 'description', head.description),
	);
	if (head.image != null) {
		tags.push(meta('itemprop', 'image', head.image));
	}
	if (head.jsonLd != null) {
		tags.push({
			kind: 'jsonld',
			key: 'jsonld',
			json: serializeStorefrontJsonLd(head.jsonLd),
		});
	}
	return tags;
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/**
 * The comment markers around the head tags in the storefront's index.html.
 * The edge function replaces everything between them with
 * `renderStorefrontHeadHtml`'s output; anything outside them is untouched.
 */
export const STOREFRONT_SEO_HEAD_START = '<!-- storefront-seo:start -->';
export const STOREFRONT_SEO_HEAD_END = '<!-- storefront-seo:end -->';

/** The data attribute marking the storefront's structured-data script. */
export const STOREFRONT_JSONLD_ATTRIBUTE = 'data-storefront-seo';

/** The tags as HTML, every value escaped, one per line. */
export function renderStorefrontHeadHtml(
	tags: ReadonlyArray<StorefrontHeadTagModel>,
): string {
	return tags
		.map((tag) => {
			switch (tag.kind) {
				case 'title':
					return `<title>${escapeHtml(tag.text)}</title>`;
				case 'meta':
					return `<meta ${tag.attribute}="${escapeHtml(tag.name)}" content="${escapeHtml(tag.content)}" />`;
				case 'canonical':
					return `<link rel="canonical" href="${escapeHtml(tag.href)}" />`;
				case 'jsonld':
					return `<script type="application/ld+json" ${STOREFRONT_JSONLD_ATTRIBUTE}>${tag.json}</script>`;
			}
		})
		.join('\n');
}
